// localStorage persistence + premium entitlement logic.
// Isolated from UI so backend/payment integration can be added later.

const KEYS = {
  ASSESSMENT: "fcs_assessment",
  CURRENT_REPORT: "fcs_current_report",
  ENTITLEMENT: "fcs_entitlement",
  HISTORY: "fcs_history",
  SYNCED: "fcs_synced_reports",
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

// ---------- Assessment ----------
export function loadAssessment() {
  return read(KEYS.ASSESSMENT, null);
}
export function saveAssessment(data) {
  write(KEYS.ASSESSMENT, data);
}

// ---------- Current report ----------
export function loadCurrentReport() {
  return read(KEYS.CURRENT_REPORT, null);
}
export function saveCurrentReport(report) {
  write(KEYS.CURRENT_REPORT, report);
}

// ---------- History ----------
export function loadHistory() {
  return read(KEYS.HISTORY, []);
}
export function addHistorySnapshot(report) {
  const history = loadHistory();
  const snapshot = {
    id: report.id,
    createdAt: report.createdAt,
    score: report.base.score,
    status: report.base.status,
    emergencyMonths: report.base.emergencyMonths,
    report,
  };
  // keep newest first, dedupe by id
  const filtered = history.filter((h) => h.id !== report.id);
  write(KEYS.HISTORY, [snapshot, ...filtered]);
}

// ---------- Entitlement ----------
export function loadEntitlement() {
  return read(KEYS.ENTITLEMENT, null);
}

export function saveEntitlement(entitlement) {
  write(KEYS.ENTITLEMENT, entitlement);
}

export function unlockPremium(reportId) {
  const now = Date.now();
  // Anchor the 7-day simulation window to ONE permanent start timestamp.
  // If the user already unlocked before, PRESERVE the original start/expiry so
  // repeating the assessment, re-generating a report, revisiting the paywall or
  // simply refreshing can never roll the trial forward (monetization bug fix).
  const existing = loadEntitlement();
  let simulationStart = now;
  if (existing && existing.premium && typeof existing.simulationStart === "number") {
    simulationStart = existing.simulationStart;
  } else if (existing && existing.premium && typeof existing.simulationExpiry === "number") {
    // Migration: legacy entitlement with only an expiry → derive its original start.
    simulationStart = existing.simulationExpiry - SEVEN_DAYS_MS;
  }
  const entitlement = {
    premium: true,
    // Keep the earliest known purchase timestamp too.
    purchaseTimestamp:
      existing && typeof existing.purchaseTimestamp === "number" ? existing.purchaseTimestamp : now,
    reportId,
    simulationStart,
    simulationExpiry: simulationStart + SEVEN_DAYS_MS,
  };
  write(KEYS.ENTITLEMENT, entitlement);
  return entitlement;
}

export function isPremium(entitlement) {
  return !!(entitlement && entitlement.premium);
}

export function isSimulationActive(entitlement, now = Date.now()) {
  return (
    isPremium(entitlement) &&
    typeof entitlement.simulationExpiry === "number" &&
    now <= entitlement.simulationExpiry
  );
}

// Whole days remaining in the (fixed) simulation window. null when not active.
export function simulationDaysLeft(entitlement, now = Date.now()) {
  if (!isSimulationActive(entitlement, now)) return null;
  return Math.ceil((entitlement.simulationExpiry - now) / SEVEN_DAYS_MS * 7);
}

// Human access label. `formatDate` is injected to avoid a UI import cycle.
export function simulationAccessLabel(entitlement, now, formatDate) {
  const n = typeof now === "number" ? now : Date.now();
  if (!isSimulationActive(entitlement, n)) return null;
  const left = simulationDaysLeft(entitlement, n);
  if (left <= 0) return "Akses simulasi berakhir hari ini.";
  if (left === 1) return "Akses simulasi berakhir besok.";
  return `Akses simulasi aktif hingga ${formatDate(entitlement.simulationExpiry)}`;
}

// ---------- Synced-to-cloud tracking (which local report ids were saved) ----------
export function loadSyncedMap() {
  return read(KEYS.SYNCED, {});
}
export function markSynced(localId, cloudId) {
  if (!localId) return;
  const m = loadSyncedMap();
  m[localId] = { cloudId: cloudId || true, at: Date.now() };
  write(KEYS.SYNCED, m);
}

export function resetAll() {
  Object.values(KEYS).forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  });
}
